import React, {useState, useEffect} from "react";
import axios from "axios";
import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";
import {bindActionCreators} from "redux";
import {connect} from "react-redux";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import * as snackbarActions from "../../actions/snackbarActions";
import MainSnackbar from "../Utils/Snackbar/SmsSnackbar";
import CustomDialog from "../Utils/Dialogs/CustomDialog";
import TextField from "../Utils/FormInputs/TextField";
import Label from "../Utils/FormInputs/Label";
import SelectInput, {CustomSelect} from "../Utils/FormInputs/SelectInput";
import {StickyHeadTable, CustomTableCell, MuiPaper} from "../Utils/Tables/StickyHeaderTable";
import {config} from "../../Helpers/env";
import {errorMessages} from "../../Helpers/ErrorMessages";

const headData = [
    {id: 'sl', label: '#', minWidth: 50},
    {id: 'name', label: 'Name', minWidth: 150},
    {id: 'type', label: 'Type', minWidth: 100},
    {id: 'phone', label: 'Phone', minWidth: 120},
    {id: 'email', label: 'Email', minWidth: 150},
    {id: 'speciality', label: 'Speciality', minWidth: 120},
    {id: 'status', label: 'Status', minWidth: 100},
    {id: 'actions', label: 'Actions', minWidth: 150},
];

const doctorTypeOptions = [
    {value: 'internal', text: 'Internal'},
    {value: 'external', text: 'External'},
];

const emptyForm = {
    first_name: '',
    last_name: '',
    doctor_type: '',
    employee_id: '',
    phone_no: '',
    email: '',
    speciality: '',
};

const ExternalSurgeonList = ({actions, snackbars}) => {
    const [surgeons, setSurgeons] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [loading, setLoading] = useState('idle');
    const [openDialog, setOpenDialog] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [submitted, setSubmitted] = useState(false);
    const [isResolved, setIsResolved] = useState(0);

    const fetchSurgeons = () => {
        setLoading('loading');
        axios.post(`${config.smsUrl}/doctors/search`, {query: ''}).then(res => {
            const data = res.data ?? {};
            const list = data.data ?? [];
            setSurgeons(list);
            setLoading('success');
        }).catch(err => {
            errorMessages(err, null, actions);
            setLoading('error');
        });
    };

    const fetchEmployees = () => {
        axios.get(`${config.smsUrl}/chumanresource/get_all_employees`).then(res => {
            const data = res.data ?? [];
            setEmployees(data);
        }).catch(() => {
            setEmployees([]);
        });
    };

    useEffect(() => {
        fetchSurgeons();
        fetchEmployees();
    }, [isResolved]);

    const employeeOptions = employees.map(emp => ({
        label: `${emp.first_name ?? ''} ${emp.last_name ?? ''}`.trim(),
        value: emp.employee_id,
    }));

    const handleOpenAdd = () => {
        setEditId(null);
        setForm(emptyForm);
        setSelectedEmployee(null);
        setSubmitted(false);
        setOpenDialog(true);
    };

    const handleOpenEdit = (surgeon) => {
        const docType = surgeon.source ?? surgeon.doctor_type ?? '';
        setEditId(surgeon.doctor_id ?? surgeon.id);
        setForm({
            first_name: surgeon.first_name ?? '',
            last_name: surgeon.last_name ?? '',
            doctor_type: docType,
            employee_id: docType === 'internal' ? (surgeon.id ?? surgeon.employee_id ?? '') : '',
            phone_no: surgeon.phone_no ?? '',
            email: surgeon.email ?? '',
            speciality: surgeon.speciality ?? '',
        });
        if (docType === 'internal') {
            const empId = surgeon.id ?? surgeon.employee_id;
            const match = employeeOptions.find(o => o.value === empId);
            setSelectedEmployee(match || null);
        } else {
            setSelectedEmployee(null);
        }
        setSubmitted(false);
        setOpenDialog(true);
    };

    const handleClose = () => {
        setOpenDialog(false);
        setEditId(null);
        setForm(emptyForm);
        setSelectedEmployee(null);
        setSubmitted(false);
    };

    const handleChange = (e) => {
        const {name, value} = e.target;
        if (name === 'doctor_type') {
            setForm(prev => ({...emptyForm, doctor_type: value}));
            setSelectedEmployee(null);
        } else {
            setForm(prev => ({...prev, [name]: value}));
        }
    };

    const handleSelectEmployee = (selected) => {
        setSelectedEmployee(selected);
        if (selected) {
            const emp = employees.find(e => e.employee_id === selected.value);
            if (emp) {
                setForm(prev => ({
                    ...prev,
                    employee_id: emp.employee_id,
                    first_name: emp.first_name ?? '',
                    last_name: emp.last_name ?? '',
                    phone_no: emp.phone_no ?? '',
                    email: emp.email ?? '',
                    speciality: emp.specialisation ?? '',
                }));
            }
        } else {
            setForm(prev => ({
                ...prev,
                employee_id: '',
                first_name: '',
                last_name: '',
                phone_no: '',
                email: '',
                speciality: '',
            }));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setSubmitted(true);

        if (!form.doctor_type) return;

        if (form.doctor_type === 'internal') {
            if (!selectedEmployee) return;
        } else {
            if (!form.first_name || !form.last_name || !form.phone_no || !form.speciality) return;
        }

        const payload = {
            first_name: form.first_name,
            last_name: form.last_name,
            doctor_type: form.doctor_type,
            phone_no: form.phone_no,
            email: form.email,
            speciality: form.speciality,
        };

        if (form.doctor_type === 'internal') {
            payload.employee_id = form.employee_id;
        }

        if (editId) {
            axios.post(`${config.smsUrl}/doctors/update/${editId}`, payload).then(() => {
                actions.snackbarActions.snackSuccess('Surgeon updated successfully');
                setIsResolved(prev => prev + 1);
                handleClose();
            }).catch(err => {
                errorMessages(err, null, actions);
            });
        } else {
            axios.post(`${config.smsUrl}/doctors/register`, payload).then(() => {
                actions.snackbarActions.snackSuccess('Surgeon registered successfully');
                setIsResolved(prev => prev + 1);
                handleClose();
            }).catch(err => {
                errorMessages(err, null, actions);
            });
        }
    };

    const handleDeactivate = (surgeon) => {
        const surgeonId = surgeon.doctor_id ?? surgeon.id;
        axios.post(`${config.smsUrl}/doctors/update/${surgeonId}`, {
            is_active: false,
        }).then(() => {
            actions.snackbarActions.snackSuccess('Surgeon deactivated successfully');
            setIsResolved(prev => prev + 1);
        }).catch(err => {
            errorMessages(err, null, actions);
        });
    };

    const closeSnackbar = () => {
        actions.snackbarActions.hideSnackbar();
    };

    const {openBar, type, message} = snackbars;
    const isLoading = loading === 'loading';
    const isSuccess = loading === 'success';
    const isError = loading === 'error';
    const isInternal = form.doctor_type === 'internal';
    const isExternal = form.doctor_type === 'external';

    return (
        <div>
            <MainSnackbar variant={type} open={openBar} message={message} handleCloseBar={closeSnackbar}/>

            <CustomDialog
                open={openDialog}
                handleClose={handleClose}
                title={editId ? 'Edit Surgeon' : 'Register Surgeon'}
                maxWidth="md"
            >
                <form onSubmit={handleSubmit} autoComplete="off">
                    <div className="row">
                        <Form.Group as={Col} md={6}>
                            <Label name="Type" htmlFor="surgeon_doctor_type" type/>
                            <SelectInput
                                name="doctor_type"
                                id="surgeon_doctor_type"
                                value={form.doctor_type}
                                onChange={handleChange}
                                options={doctorTypeOptions}
                                defaultOption="Select Type"
                                submitted={submitted}
                            />
                        </Form.Group>

                        {isInternal && (
                            <Form.Group as={Col} md={6}>
                                <Label name="Employee" type/>
                                <CustomSelect
                                    id="surgeon_employee"
                                    value={selectedEmployee}
                                    onChange={handleSelectEmployee}
                                    options={employeeOptions}
                                    submitted={submitted}
                                />
                                {submitted && !selectedEmployee &&
                                    <div className="help-block">Please select an employee</div>}
                            </Form.Group>
                        )}

                        {isExternal && (
                            <>
                                <Form.Group as={Col} md={6}>
                                    <Label name="First Name" htmlFor="surgeon_first_name" type/>
                                    <TextField
                                        type="text"
                                        name="first_name"
                                        id="surgeon_first_name"
                                        value={form.first_name}
                                        onChange={handleChange}
                                        submitted={submitted}
                                    />
                                    {submitted && !form.first_name &&
                                        <div className="help-block">First name is required</div>}
                                </Form.Group>
                                <Form.Group as={Col} md={6}>
                                    <Label name="Last Name" htmlFor="surgeon_last_name" type/>
                                    <TextField
                                        type="text"
                                        name="last_name"
                                        id="surgeon_last_name"
                                        value={form.last_name}
                                        onChange={handleChange}
                                        submitted={submitted}
                                    />
                                    {submitted && !form.last_name &&
                                        <div className="help-block">Last name is required</div>}
                                </Form.Group>
                                <Form.Group as={Col} md={6}>
                                    <Label name="Phone" htmlFor="surgeon_phone_no" type/>
                                    <TextField
                                        type="text"
                                        name="phone_no"
                                        id="surgeon_phone_no"
                                        value={form.phone_no}
                                        onChange={handleChange}
                                        submitted={submitted}
                                    />
                                    {submitted && !form.phone_no &&
                                        <div className="help-block">Phone number is required</div>}
                                </Form.Group>
                                <Form.Group as={Col} md={6}>
                                    <Label name="Email" htmlFor="surgeon_email"/>
                                    <TextField
                                        type="email"
                                        name="email"
                                        id="surgeon_email"
                                        value={form.email}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                                <Form.Group as={Col} md={6}>
                                    <Label name="Speciality" htmlFor="surgeon_speciality" type/>
                                    <TextField
                                        type="text"
                                        name="speciality"
                                        id="surgeon_speciality"
                                        value={form.speciality}
                                        onChange={handleChange}
                                        submitted={submitted}
                                    />
                                    {submitted && !form.speciality &&
                                        <div className="help-block">Speciality is required</div>}
                                </Form.Group>
                            </>
                        )}
                    </div>
                    <div className="text-right mt-3">
                        <button type="button" className="btn btn-sm sms-grey-btn mr-2" onClick={handleClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-sm sms-btn">
                            {editId ? 'Update Surgeon' : 'Register Surgeon'}
                        </button>
                    </div>
                </form>
            </CustomDialog>

            <div className="text-right mb-3" style={{paddingTop: '1rem'}}>
                <button className="btn btn-sm sms-info-btn" onClick={handleOpenAdd}>
                    Register Surgeon
                </button>
            </div>

            <MuiPaper>
                <StickyHeadTable
                    width="100%"
                    columns={headData}
                >
                    {isLoading ? (
                        <TableRow>
                            <TableCell align="center" colSpan={headData.length}>
                                <img src="/images/smsloader.gif" alt="loader"/>
                            </TableCell>
                        </TableRow>
                    ) : null}
                    {isSuccess ? (
                        surgeons.length > 0 ? (
                            surgeons.map((item, index) => (
                                <TableRow tabIndex={-1} key={item.doctor_id ?? item.id ?? index}>
                                    <CustomTableCell>{index + 1}</CustomTableCell>
                                    <CustomTableCell>{`${item.first_name ?? ''} ${item.last_name ?? ''}`.trim()}</CustomTableCell>
                                    <CustomTableCell>
                                        {item.source === 'internal'
                                            ? <span className="badge badge-info">Internal</span>
                                            : <span className="badge badge-warning">External</span>
                                        }
                                    </CustomTableCell>
                                    <CustomTableCell>{item.phone_no}</CustomTableCell>
                                    <CustomTableCell>{item.email}</CustomTableCell>
                                    <CustomTableCell>{item.speciality}</CustomTableCell>
                                    <CustomTableCell>
                                        {item.is_active !== false
                                            ? <span className="badge badge-success">Active</span>
                                            : <span className="badge badge-danger">Inactive</span>
                                        }
                                    </CustomTableCell>
                                    <CustomTableCell>
                                        <button
                                            className="btn btn-sm sms-info-btn mr-1"
                                            onClick={() => handleOpenEdit(item)}
                                        >
                                            Edit
                                        </button>
                                        {item.is_active !== false && (
                                            <button
                                                className="btn btn-sm sms-btn-dismiss"
                                                onClick={() => handleDeactivate(item)}
                                            >
                                                Deactivate
                                            </button>
                                        )}
                                    </CustomTableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell align="center" colSpan={headData.length}>
                                    No surgeons found
                                </TableCell>
                            </TableRow>
                        )
                    ) : null}
                    {isError ? (
                        <TableRow>
                            <TableCell align="center" colSpan={headData.length}>
                                The server did not return a valid response
                            </TableCell>
                        </TableRow>
                    ) : null}
                </StickyHeadTable>
            </MuiPaper>
        </div>
    );
};

function mapStateToProps(state) {
    return {snackbars: state.snackbar};
}

function mapDispatchToProps(dispatch) {
    return {actions: {snackbarActions: bindActionCreators(snackbarActions, dispatch)}};
}

export default connect(mapStateToProps, mapDispatchToProps)(ExternalSurgeonList);
